import {
  getApp,
  getApps,
  initializeApp,
  type FirebaseApp,
} from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  type AppCheck,
} from "firebase/app-check";
import {
  connectAuthEmulator,
  getAuth,
  inMemoryPersistence,
  initializeAuth,
  setPersistence,
  browserLocalPersistence,
  type Auth,
} from "firebase/auth";
import {
  connectFirestoreEmulator,
  getFirestore,
  type Firestore,
} from "firebase/firestore";
import {
  connectStorageEmulator,
  getStorage,
  type FirebaseStorage,
} from "firebase/storage";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === "true";

// reCAPTCHA v3 site key — App Check provider (A1 FAZA A audyt 2026-05-12).
// Public (idzie do bundle), Secret Key zostaje w Google reCAPTCHA Admin.
const RECAPTCHA_SITE_KEY = "6LdeYOYsAAAAAIp7TLQhiMnQmrPYNCNiuKf7oP7K";

// Debug token mode dla dev (Vite dev server localhost) — Firebase SDK
// printuje token w Console przy pierwszym getToken. User dodaje go w
// Firebase Console → App Check → Manage debug tokens, wtedy lokalny dev
// dostaje atestację bez prawdziwego reCAPTCHA challenge.
// MUSI być ustawiony PRZED initializeAppCheck.
if (
  import.meta.env.DEV &&
  typeof window !== "undefined" &&
  !useEmulators
) {
  // @ts-expect-error: Firebase debug-only global
  globalThis.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

let appCached: FirebaseApp | null = null;
let appCheckCached: AppCheck | null = null;
let authCached: Auth | null = null;
let dbCached: Firestore | null = null;
let storageCached: FirebaseStorage | null = null;

export function getFirebaseApp(): FirebaseApp {
  if (appCached) return appCached;
  appCached = getApps()[0] ?? initializeApp(firebaseConfig);
  // App Check init MUSI być natychmiast po Firebase App, przed jakimkolwiek
  // serwisem (Firestore, Storage, Auth). Inaczej pierwsze requesty mogą
  // wyjść bez App Check token'a.
  if (typeof window !== "undefined" && !useEmulators && !appCheckCached) {
    try {
      appCheckCached = initializeAppCheck(appCached, {
        provider: new ReCaptchaV3Provider(RECAPTCHA_SITE_KEY),
        // Auto-refresh tokenów (default 1h TTL) zanim wygasną.
        isTokenAutoRefreshEnabled: true,
      });
    } catch (e) {
      // Idempotent: hot reload w devie może re-init.
      console.warn("[firebase] App Check init failed:", e);
    }
  }
  return appCached;
}

export function getAppCheckInstance(): AppCheck | null {
  // Ensure firebase app initialized first (triggers App Check init).
  getFirebaseApp();
  return appCheckCached;
}

export function getFirebaseAuth(): Auth {
  if (authCached) return authCached;
  const auth = getAuth(getFirebaseApp());
  // SSR-safe persistence: in-memory na serwerze, localStorage w przeglądarce
  if (typeof window === "undefined") {
    void setPersistence(auth, inMemoryPersistence);
  } else {
    void setPersistence(auth, browserLocalPersistence);
  }
  if (useEmulators && typeof window !== "undefined") {
    try {
      connectAuthEmulator(auth, "http://127.0.0.1:9099", {
        disableWarnings: true,
      });
    } catch {
      // Idempotent: już podłączone w hot-reloadzie
    }
  }
  authCached = auth;
  return auth;
}

export function getDb(): Firestore {
  if (dbCached) return dbCached;
  const db = getFirestore(getFirebaseApp());
  if (useEmulators && typeof window !== "undefined") {
    try {
      connectFirestoreEmulator(db, "127.0.0.1", 8080);
    } catch {
      // Idempotent
    }
  }
  dbCached = db;
  return db;
}

export function getFirebaseStorage(): FirebaseStorage {
  if (storageCached) return storageCached;
  const storage = getStorage(getFirebaseApp());
  if (useEmulators && typeof window !== "undefined") {
    try {
      connectStorageEmulator(storage, "127.0.0.1", 9199);
    } catch {
      // Idempotent
    }
  }
  storageCached = storage;
  return storage;
}

/**
 * Drugorzędna instancja Firebase Auth — używana TYLKO do zakładania kont
 * dzieci, żeby `createUserWithEmailAndPassword` nie wylogował zalogowanego
 * rodzica z głównej instancji.
 *
 * Uwaga: NIE używa persistence (in-memory). Po dodaniu konta wywołaj
 * `signOut(secondaryAuth)`.
 */
const SECONDARY_NAME = "secondary";

export function getSecondaryAuth(): Auth {
  const existing = getApps().find((a) => a.name === SECONDARY_NAME);
  const app: FirebaseApp = existing ?? initializeApp(firebaseConfig, SECONDARY_NAME);
  // initializeAuth tylko raz — przy następnym wywołaniu pobierz istniejące przez getAuth
  let auth: Auth;
  try {
    auth = initializeAuth(app, { persistence: inMemoryPersistence });
  } catch {
    auth = getAuth(getApp(SECONDARY_NAME));
  }
  if (useEmulators && typeof window !== "undefined") {
    try {
      connectAuthEmulator(auth, "http://127.0.0.1:9099", {
        disableWarnings: true,
      });
    } catch {
      // Idempotent
    }
  }
  return auth;
}
